import { SettingType } from '@fluidx/office-fluid-types';

/**
 * `EcsClientConfiguration` containing information about the `EcsClient` behavior.
 * Note: `EcsClient` is an internal class inside the @fluidx/settings-provider package
 */
export interface EcsClientConfiguration {
  /**
   * Host targeted by the request to fetch settings from ECS.
   */
  host: string;

  /**
   * Client Name of the request to fetch settings from ECS.
   */
  clientName: string;

  /**
   * Client Version of the request to fetch settings from ECS.
   */
  clientVersion: string;

  /**
   * Filters that are going to be appended as query params to the request to fetch settings from ECS.
   */
  filters: EcsFilters;

  /**
   * A list containing ECS Teams from the ECS Client (from the `clientName` property) to connect.
   * E.g. `['TeamName']` or `['TeamName1', 'TeamName2']`
   */
  teamNames: string[];

  /**
   * This string is used as a top level LocalStorage cache key that we use to store responses from ECS.
   * Note that there may be multiple responses stored in the cache for different configurations
   * (e.g., for two different users or two different environments). Thus the data for the current user
   * is stored in JSON.parse(localStorage[responseCacheKey])[cacheKey]
   */
  responsesCacheKey: string;

  /**
   * This string is used to store the EcsResponse in LocalStorage cache for the current `EcsClientConfiguration`
   * Note that there may be multiple responses stored in the cache for different configurations
   * (e.g., for two different users or two different environments). Thus the data for the current user
   * is stored in JSON.parse(localStorage[responseCacheKey])[cacheKey]
   */
  cacheKey: string;

  /**
   * Number representing the minutes a cached ECS response is valid starting at the moment that ECS responded.
   * If provided, this overrides the ECS default of 1 hour (60min).
   */
  cacheExpiresDurationOverrideInMin?: number;

  /**
   * If provided, the `EcsClient` is going to use this sessionId to decide wether or not the response stored in cache
   * belongs to the same sessionId, if not, it will start a fetch to refresh the settings, ignoring expiration.
   */
  sessionId?: string;

  /**
   * A list containing ECS Teams from other ECS Clients to connect.
   * E.g. `['TeamName(ClientName)']` or `['TeamName1(ClientName)','TeamName2(ClientName)']`
   */
  guestAgents?: string[];
}

export interface EcsTeamSettings {
  [settingName: string]: SettingType;
}

/**
 * ECS response from an ECS fetch.
 * Be sure to bump the cacheVersion inside `\src\EcsClientConstants.ts` whenever a change is made to this interface
 */
export interface EcsResponse {
  /**
   * Settings for all the fetched ECS Teams.
   */
  ecsTeamsSettings: { [teamName: string]: EcsTeamSettings };

  /**
   * Can be used to determine if a request is returning a Not Modified status.
   */
  eTag: string;

  /**
   * Cache version used for `LocalStorage` cleanup.
   */
  cacheVersion?: number;

  /**
   * Contains the Ids that can be used to trace back features and experiments from ECS.
   */
  configIDs?: { [teamName: string]: string };

  /*
   * Indicates the date and time after which a response is considered stale.
   */
  expires?: Date;

  /*
   * Indicates the sessionId that retrieved this response from ECS.
   * Used to decide wether we should fetch in the background to refresh settings or not.
   */
  sessionId?: string;
}

/**
 * Represents a setting and it's source
 */
export interface EcsSetting {
  settingValue: SettingType;
  ecsSettingSource: EcsSettingSource;
}

/**
 * Interface that helps locate a specific setting
 */
export interface EcsSettingLocator {
  teamName: string;
  settingName: string;
  stringifiedLocator: string;
}

/**
 * The origin of a setting.
 */
export enum EcsSettingSource {
  LocalStorageOverride = 'LocalStorageOverride',
  QueryParamOverride = 'QueryParamOverride',
  Default = 'Default',
  CachedServiceResponse = 'CachedServiceResponse'
}

/**
 * Filters to be added to the ECS request as query parameters.
 * These filters are used on ECS side for assignment and allocation.
 * E.g. a configuration is only served for a specific browserName.
 */
export interface EcsFilters {
  [key: string]: any;
}
